import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import Cookies from 'js-cookie';
import { delay, Observable, of, throwError } from 'rxjs';

import { CompanyGradeExpirationCode } from '#data/CompanyGradeExpirationCode.enum';
import { ESG_GERL_QUIZ_COMMENT_TEMPLATE } from '#data/esg-gerl-quiz-comment';
import { ControllerStatus } from '#models/ControllerStatus.enum';
import { GerlRejectCode } from '#models/Gerl.model';
import { ControllerCode, ControllerDataMapping, DocumentMetadata, DocumentStatus } from '#models/MicroController.model';
import { CheckResult } from '#services/cve/CheckResult';
import { ControllerDetails } from '#services/cve/ControllerDetails';
import { IterationStatusCode, LineOfBusiness, VettingIteration } from '#services/cve/VettingIteration';

import { companyDetail113, companyDetail114 } from './fixtures/companyDetail.fixture';
import { companySearchResults } from './fixtures/companySearchResults.fixture';
import { countriesData } from './fixtures/countries.fixture';
import { getMocksByScenarios, HttpMock } from './utils/mockScenarios';
import { MOCK_SCENARIOS_COOKIE_NAME } from './utils/mockScenariosCookieUtils';

const HTTP_RESPONSES_DELAY: number | undefined = undefined;

const iterationControlsState: ControllerDetails[] = [
  {
    controlCode: 'BuExchangeGroup',
    statusCode: ControllerStatus.CHECK_ERROR,
    check: {
      requestDateTime: '2024-05-06T08:16:12.712Z',
      requestorId: 'CVE',
      controlData: {
        requestData: {
          buCode: 'SIAC',
          context: 'proposal',
          automaticBehaviorConfig: {
            successAutomaticResponseCode: 'VALIDATE',
          },
        },
        errorDetails: 'Error message',
      },
      responseDateTime: '2024-05-06T08:16:13.921Z',
      expirationDateTime: '2025-11-06T08:16:13.845Z',
      requestDocumentIds: [],
      responseDocumentIds: [],
    },
  },
  {
    controlCode: 'CompanyGrade',
    statusCode: ControllerStatus.ESCALATING,
    check: {
      requestDateTime: '2024-05-18T13:29:56.409Z',
      responseDateTime: '2024-05-18T13:29:56.409Z',
      requestorId: 'CVE',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        companyId: '113',
        expirationCode: CompanyGradeExpirationCode.LAST_REFRESH_UP_TO_DATE,
        grade: {
          gradeCode: '09',
          lastRefreshDate: '2023-09-26',
        },
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
    escalation: {
      requestDateTime: '2024-05-18T13:29:56.409Z',
      requestorId: 'CVE',
      controlData: {
        companyId: '113',
        internalTicketId: 'ticket-id',
      },
      requestDocumentIds: ['uid1'],
    },
  },
  {
    controlCode: 'EconomicSanctions',
    statusCode: ControllerStatus.CHECK_REJECTED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2024-05-21T13:29:56.409Z',
      controlData: {
        documents: [
          {
            documentStatusCode: DocumentStatus.PROCESSING_FINISHED,
            documentUid: '6b947463-c434-47f5-a714-3e74e233a886',
            creationDateTime: '2024-10-14T07:32:45.642Z',
            fileName: 'celebi.png',
            mimeTypeCode: 'image/png',
          },
          {
            documentStatusCode: DocumentStatus.IN_PROCESSING,
            documentUid: '6b947463-c434-47f5-a714-3e74e233a888',
            creationDateTime: '2024-10-14T07:32:45.642Z',
            fileName: 'lugia.png',
            mimeTypeCode: 'image/png',
          },
          {
            documentStatusCode: DocumentStatus.FAILED,
            documentUid: '6b947463-c434-47f5-a714-3e74e233a887',
            creationDateTime: '2024-10-14T07:32:45.642Z',
            fileName: 'mewtwo.png',
            mimeTypeCode: 'image/png',
          },
          {
            documentStatusCode: DocumentStatus.DELETED_LOGICALLY,
            documentUid: '6b947463-c434-47f5-a714-3e74e233a889',
            creationDateTime: '2024-10-14T07:32:45.642Z',
            fileName: 'wartortle.png',
            mimeTypeCode: 'image/png',
          },
        ] satisfies DocumentMetadata[],
        sanctions: [
          {
            companyId: '113',
            sanctionId: '115250479-20230317135606274587',
            sanctionSource: {
              originCode: 'Manual creation',
              updateOriginCode: 'Manual creation',
            },
            sanctionTypeCode: 'SP',
            sanctionUpdateDateTime: '2023-03-17',
            sanctionCreationDate: '2023-03-17',
            isContinuatedSanction: true,
            sanctionEffectiveEndDate: '2028-03-17',
            isActive: true,
          },
        ],
        consecutiveSanctionsEndDate: '2028-03-17',
        rejectCode: 'ONGOING_SANCTION',
        escalationContact: 'roland.gasnier@eulerhermes.com',
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
  {
    controlCode: 'SensitiveBusiness',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        companyId: '113',
        tradeSectors: [{ code: '1234', isMain: true }],
        escalationContact: 'test@allianz-trade.com',
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
  {
    controlCode: 'SensitiveCountry',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        countryCode: 'RU',
        escalationContact: 'test@allianz-trade.com',
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
  {
    controlCode: 'EnergyPolicy',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        companyId: '113',
        tradeSectors: [
          { code: '4265', isMain: false },
          { code: '1234', isMain: true },
          { code: '9856', isMain: false, isRestricted: true },
        ],
        escalationContact: 'test@allianz-trade.com',
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
  {
    controlCode: 'PoliticalExposedPerson',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestDateTime: '2024-05-18T13:29:56.409Z',
      responseDateTime: '2024-05-18T13:29:56.409Z',
      requestorId: 'CVE',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        requestData: {
          buCode: 'SFAC',
        },
        escalationContact: 'test@allianz-trade.com',
      },
    },
  },
  {
    controlCode: 'Gerl',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        companyId: '113',
        escalationContact: 'test@allianz-trade.com',
        gerl: {
          lastUpdateDate: '2024-05-20',
          isGerlCompliant: true,
          isUpGerlCompliant: undefined,
          isUltimateParent: false,
          isRelatedCompaniesListed: false,
          companies: [
            {
              id: '113',
              type: 'MAIN',
            },
          ],
        } satisfies ControllerDataMapping['Gerl']['gerl'],
        rejectCode: GerlRejectCode.ULTIMATE_PARENT_COMPANY_UNKNOWN,
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
  {
    controlCode: 'ReputationalRisk',
    statusCode: ControllerStatus.MANUAL_CHECK_REQUIRED,
    check: {
      requestorId: 'CVE',
      requestDateTime: '2024-05-21T13:29:56.409Z',
      responseDateTime: '2024-05-21T13:29:56.409Z',
      expirationDateTime: '2025-05-21T13:29:56.409Z',
      controlData: {
        companyId: '113',
        escalationContact: 'test@allianz-trade.com',
        requestData: {
          buCode: 'SFAC',
        },
      },
    },
  },
];

function requestMatched(req: HttpRequest<unknown>, mock: HttpMock) {
  const parseableUrl = req.url.startsWith('/') ? `https://dummy${req.url}` : req.url;
  const urlPath = new URL(parseableUrl).pathname;

  return req.method === mock.request.method && urlPath.match(mock.request.urlPathPattern);
}

@Injectable()
export class MockInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const response = this.interceptForScenarios(req) ?? this.interceptForDefault(req);

    if (!response) {
      console.warn('Request not intercepted:', req.method, req.url, 'params', req.params, 'body', req.body);
      return next.handle(req);
    }

    console.log(
      req.method,
      req.url,
      'params',
      req.params,
      'body',
      req.body,
      'headers',
      req.headers,
      'response',
      response,
    );

    if (response instanceof HttpErrorResponse) {
      return throwError(() => response);
    }
    return of(response).pipe(delay(HTTP_RESPONSES_DELAY ?? 0));
  }

  private interceptForScenarios(req: HttpRequest<unknown>): HttpResponse<unknown> | HttpErrorResponse | undefined {
    const scenariosNames = Cookies.get(MOCK_SCENARIOS_COOKIE_NAME);
    if (!scenariosNames) {
      return undefined;
    }

    const scenariosMocks = getMocksByScenarios(scenariosNames.split(','));
    const mock = scenariosMocks.find((mock) => requestMatched(req, mock));
    if (!mock) return;

    if (mock.response.status >= 400) {
      return new HttpErrorResponse({
        status: mock.response.status,
        url: req.url,
        headers: new HttpHeaders(mock.response.headers),
      });
    }

    return new HttpResponse({ ...mock.response, headers: new HttpHeaders(mock.response.headers) });
  }

  private interceptForDefault(req: HttpRequest<unknown>): HttpResponse<unknown> | undefined {
    if (req.method === 'GET') {
      return this.interceptGetDefault(req);
    } else if (req.method === 'POST') {
      return this.interceptPostDefault(req);
    } else if (req.method === 'PATCH') {
      return this.interceptPatchDefault(req);
    } else if (req.method === 'PUT') {
      return this.interceptPutDefault(req);
    }
    return undefined;
  }

  private interceptGetDefault(req: HttpRequest<unknown>): HttpResponse<unknown> | undefined {
    if (/\/customerVettings\?/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: [
          {
            customerVettingId: 'cveId',
            lineOfBusiness: 'TCI',
            companyId: '113',
            hasOpenIteration: true,
            iterations: [
              { creatorId: 'CCHASLES', iterationId: 'iterationId', statusCode: IterationStatusCode.CREATED },
            ],
          },
        ],
      });
    }
    if (/\/iterations\/iterationId$/.exec(req.url)) {
      return new HttpResponse<VettingIteration>({
        status: 200,
        body: {
          iterationId: 'iterationId',
          lineOfBusiness: LineOfBusiness.TCI,
          companyId: '113',
          statusCode: IterationStatusCode.CREATED,
          creation: {
            creationDateTime: '2024-04-19T10:05:42.156Z',
            creatorId: 'CVE',
            ownerId: 'CVE',
          },
          documentIds: ['uid1', 'uid2'],
          customProperties: {
            supportTicketId: 'support-ticket-id',
            supportRequestData: {
              customerActivityDescription:
                'Lorem \nipsum dolor sit amet\n, consectetur adipiscing\n elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            },
            esgGerlQuizComment: ESG_GERL_QUIZ_COMMENT_TEMPLATE,
          },
          controls: iterationControlsState,
        },
      });
    }
    if (/\/iterations\/iterationId\/controls/.exec(req.url)) {
      let controlDetails: ControllerDetails | undefined;
      if (/\/CompanyGrade/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'CompanyGrade');
      } else if (/\/PoliticalExposedPerson/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'PoliticalExposedPerson');
      } else if (/\/EconomicSanctions/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'EconomicSanctions');
      } else if (/\/SensitiveBusiness/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'SensitiveBusiness');
      } else if (/\/EnergyPolicy/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'EnergyPolicy');
      } else if (/\/ReputationalRisk/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'ReputationalRisk');
      } else if (/\/SensitiveCountry/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'SensitiveCountry');
      } else if (/\/Gerl/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'Gerl');
      } else if (/\/BuExchangeGroup/.exec(req.url)) {
        controlDetails = iterationControlsState.find((control) => control.controlCode === 'BuExchangeGroup');
      } else {
        controlDetails = {
          statusCode: ControllerStatus.CHECK_SUCCESS,
          controlCode: /\/iterations\/iterationId\/controls\/(.*)/.exec(req.url)?.[1] as ControllerCode,
          check: {
            requestorId: 'CVE',
            requestDateTime: '2024-05-21T13:29:56.409Z',
            responseDateTime: '2024-05-21T13:29:56.409Z',
            expirationDateTime: '2025-05-21T13:29:56.409Z',
            controlData: {
              companyId: '113',
              escalationContact: 'test@allianz-trade.com',
            },
          },
        };
      }
      return new HttpResponse<ControllerDetails>({
        status: 200,
        body: controlDetails,
      });
    }
    if (/\/companies\/114/.exec(req.url)) {
      return new HttpResponse({ status: 200, body: companyDetail114 });
    }
    if (/\/companies\/\d*/.exec(req.url)) {
      return new HttpResponse({ status: 200, body: companyDetail113 });
    }
    if (/\/countries/.exec(req.url)) {
      return new HttpResponse({ status: 200, body: countriesData });
    }
    if (/\/tradeSectors/.exec(req.url)) {
      let label: string;
      if (/1234/.exec(req.url)) {
        label = 'UFO Investigation';
      } else if (/4265/.exec(req.url)) {
        label = 'Spellbook Publishing';
      } else {
        label = 'Unicorns Preservation';
      }
      return new HttpResponse({
        status: 200,
        body: {
          tradeSectorLabel: label,
        },
      });
    }
    if (/\/internalUsers/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: [{ businessEmail: 'johndoe@gmail.fr', businessUnit: 'FRANCE' }],
      });
    }
    if (/documents\/(.*)\/metadata/.exec(req.url)) {
      const documentUid = /documents\/(.*)\/metadata/.exec(req.url)?.[1];
      let documentStatusCode = DocumentStatus.PROCESSING_FINISHED;
      if (documentUid?.includes('failed')) {
        documentStatusCode = DocumentStatus.FAILED;
      } else if (documentUid?.includes('pending')) {
        documentStatusCode = DocumentStatus.IN_PROCESSING;
      } else if (documentUid?.includes('deleted')) {
        documentStatusCode = DocumentStatus.DELETED_PHYSICALLY;
      }
      return new HttpResponse({
        status: 200,
        body: {
          documentUid,
          documentStatusCode,
          creationDateTime: '2024-10-14T07:32:45.642Z',
          fileName: `image_${documentUid}.png`,
          mimeTypeCode: 'image/png',
        },
      });
    }
    if (/\/iterations\?/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        headers: new HttpHeaders({
          'total-items': '3',
        }),
        body: [
          {
            iterationId: 'iterationId',
            lineOfBusiness: LineOfBusiness.TCI,
            statusCode: 'CREATED',
            creation: {
              creatorId: 'CCHASLES',
              creationDateTime: '2024-09-09T13:26:26.851Z',
              ownerId: 'CCHASLES',
            },
            companyId: '113',
            controls: [
              {
                controlCode: 'CompanyGrade',
                statusCode: 'NOT_STARTED',
              },
            ],
          },
          {
            iterationId: 'iterationId2',
            statusCode: 'CANCELLED',
            creation: {
              creatorId: 'CCHASLES',
              creationDateTime: '2024-09-09T13:26:26.851Z',
              ownerId: 'CCHASLES',
            },
            companyId: '114',
            controls: [
              {
                controlCode: 'CompanyGrade',
                statusCode: 'NOT_STARTED',
              },
            ],
          },
          {
            iterationId: 'iterationId3',
            statusCode: 'VALIDATED',
            creation: {
              creatorId: 'CCHASLES',
              creationDateTime: '2024-09-09T13:26:26.851Z',
              ownerId: 'CCHASLES',
            },
            companyId: '115',
            controls: [
              {
                controlCode: 'CompanyGrade',
                statusCode: 'NOT_STARTED',
              },
            ],
          },
          {
            iterationId: 'iterationId4',
            statusCode: 'REJECTED',
            creation: {
              creatorId: 'RDUFOSSE',
              creationDateTime: '2024-09-09T13:26:26.851Z',
              ownerId: 'RDUFOSSE',
            },
            companyId: '116',
            controls: [
              {
                controlCode: 'CompanyGrade',
                statusCode: 'ESCALATION_REJECTED',
              },
            ],
          },
        ],
      });
    }
    if (/\/v3\/internalTickets/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: {
          statusCode: 9200002,
        },
      });
    }
    return undefined;
  }

  private interceptPatchDefault(req: HttpRequest<unknown>): HttpResponse<unknown> | undefined {
    if (/\/iterations\/iterationId$/.exec(req.url)) {
      return new HttpResponse({
        status: 204,
      });
    }
    return undefined;
  }

  private interceptPutDefault(_req: HttpRequest<unknown>): HttpResponse<unknown> | undefined {
    return new HttpResponse({
      status: 200,
      body: {},
    });
  }

  private interceptPostDefault(req: HttpRequest<unknown>): HttpResponse<unknown> | undefined {
    if (/\/iterations\/iterationId\/check/.exec(req.url)) {
      return new HttpResponse<CheckResult[]>({
        status: 207,
        body: [{ statusCode: 'SUCCESS', controlCode: 'controlCode' }],
      });
    }
    if (/\/iterations\/iterationId\/controls\/\w+\/process/.exec(req.url)) {
      return new HttpResponse<CheckResult>({
        status: 202,
        body: { statusCode: 'SUCCESS', controlCode: 'controlCode' },
      });
    }
    if (/\/iterations\/iterationId\/controls\/\w+\/acknowledge/.exec(req.url)) {
      return new HttpResponse({
        status: 204,
      });
    }
    if (/\/iterations\/iterationId\/controls\/\w+\/check\/respond/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: {
          documentsData: [],
        },
      });
    }
    if (/\/iterations\/iterationId\/documents/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: {
          documentPresignedUrls: [
            {
              documentId: 'uid1',
              presignedUrl: 'http://localhost:3000/put-document',
            },
            {
              documentId: 'uid2',
              presignedUrl: 'http://localhost:3000/put-document',
            },
            {
              documentId: 'uid3',
              presignedUrl: 'http://localhost:3000/put-document',
            },
          ],
        },
      });
    }
    if (/\/iterations\/iterationId\/close/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
      });
    }
    if (/\/escalation\/respond/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
      });
    }
    if (/\/customerVettings/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: {
          customerVettingId: 'cveId',
          iterationId: 'iterationId',
        },
      });
    }
    if (/\/customerVettingJourney/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: {
          customerVettingId: 'cveId',
          iterationId: 'iterationId',
        },
      });
    }
    if (/\/advancedSearch/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: companySearchResults,
      });
    }
    if (/\/v3\/permissions\/actions\/search/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: ['jade_read_customer_vetting_document', 'jade_write_customer_vetting_document'],
      });
    }
    if (/\/v3\/internalTickets$/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
        body: { internalTicketId: 'internal-ticket-id' },
      });
    }
    if (/\/safeRenewal\/bulk/.exec(req.url)) {
      return new HttpResponse({
        status: 200,
      });
    }
    return undefined;
  }
}
